import { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';

import { useCompany } from '@/contexts/CompanyContext';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { MultiSelect } from '@/components/ui/multiselect';
import {
  Search,
  Filter,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  TrendingUp,
  FileText,
  Download,
  Link2Off,
  Settings2,
  Banknote,
  CircleDashed,
  Ban,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { KPI } from '@/components/ui/kpi';
import type { PurchaseRequest, PurchaseRequestStatus, PurchaseRequestSummary } from '@/types/purchase-request';
import {
  groupPurchaseRequests,
  PR_GROUP_BY_OPTIONS,
  type PRGroupById,
  type PurchaseRequestGroup,
} from '@/lib/purchase-group-by-utils';
import type { Employee } from '@/types/index';
import { fetchPurchaseRequests } from '@/lib/mock-purchase-requests';
import { fetchFinOpsPurchaseRequests, fetchFinOpsPurchaseRequestDetails, isFinOpsIntegrationAvailable } from '@/lib/finops-api';
import { getDataMode } from '@/lib/data-service';
import { format } from 'date-fns';
import { PurchaseRequestFullModal } from './purchase-request-full-modal';


// DataTable + pagination
import { DataTableEnhanced, type ColumnDef, type ColumnSort, columnDefinitionsToVisibility } from '@/components/ui/data-table-enhanced';
import { PaginationControls } from '@/components/pagination-controls';
import { calculatePagination, paginateArray } from '@/types/pagination';
import type { PaginationMetadata } from '@/types/pagination';

// Column configurator + preferences
import { ColumnConfiguratorDrawer, type ColumnDefinition } from '@/components/column-configurator-drawer';
import { useColumnPreferences } from '@/hooks/use-column-preferences';

// Advanced filter sheet
import {
  PurchaseRequestFilterSheet,
  type PRFilters,
  countActivePRFilters,
  applyPRFilters,
} from './purchase-request-filter-sheet';

interface PurchasesCommitmentsTabProps {
  projectId: number;
  projectCode?: string;
  instanceId?: string;
  employees?: Employee[];
}

// ─── Status config ─────────────────────────────────────────────────────────────

const statusConfig: Record<string, {
  variant: 'success' | 'warning' | 'destructive' | 'default';
  accent: 'success' | 'warning' | 'destructive' | 'info';
  icon: typeof CheckCircle2;
}> = {
  Approved:            { variant: 'success',      accent: 'success',     icon: CheckCircle2 },
  Pending:             { variant: 'warning',      accent: 'warning',     icon: Clock },
  'Awaiting Approval': { variant: 'warning',      accent: 'warning',     icon: Clock },
  Rejected:            { variant: 'destructive',  accent: 'destructive', icon: XCircle },
  Posted:              { variant: 'default',      accent: 'info',        icon: CheckCircle2 },
  Redirected:          { variant: 'warning',      accent: 'warning',     icon: AlertCircle },
  Reopened:            { variant: 'warning',      accent: 'warning',     icon: AlertCircle },
  Cancelled:           { variant: 'destructive',  accent: 'destructive', icon: XCircle },
  Draft:               { variant: 'default',      accent: 'info',        icon: FileText },
};

const getStatusConfig = (status: string) =>
  statusConfig[status] ?? { variant: 'default' as const, accent: 'info' as const, icon: AlertCircle };

// ─── PR helpers ────────────────────────────────────────────────────────────────

const calculatePRAge = (request: PurchaseRequest): number | null => {
  const finalStatuses = ['Approved', 'Posted', 'Rejected', 'Cancelled'];
  if (finalStatuses.includes(request.status)) return null;
  const diffTime = Math.abs(Date.now() - new Date(request.date).getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const getCurrentApprovalStage = (request: PurchaseRequest): string => {
  if (!request.approvalHistory || request.approvalHistory.length === 0) return '';
  const sorted = [...request.approvalHistory].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  return sorted[0].stage || '';
};

// Returns the pending approver(s) for in-flight requests (toUser of the most recent entry)
const getCurrentApprover = (request: PurchaseRequest): string => {
  const inFlight = ['Pending', 'Awaiting Approval', 'Reopened', 'Redirected'];
  if (!inFlight.includes(request.status)) return '';
  if (!request.approvalHistory || request.approvalHistory.length === 0) return '';
  const sorted = [...request.approvalHistory].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  const toUser = sorted[0].toUser;
  if (!toUser) return '';
  return Array.isArray(toUser) ? toUser.join(', ') : toUser;
};

// Returns who last acted on the request (byUser of the most recent history entry)
const getLastApprover = (request: PurchaseRequest): string => {
  if (!request.approvalHistory || request.approvalHistory.length === 0) return '';
  const sorted = [...request.approvalHistory].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  return sorted[0].byUser || '';
};

// ─── Default column definitions ────────────────────────────────────────────────

const DEFAULT_PR_COLUMNS: ColumnDefinition[] = [
  { id: 'requestId',       name: 'Request ID',       description: 'Purchase request identifier',                                  category: 'Status',    visible: true,  locked: true,  order: 0  },
  { id: 'orderNumber',     name: 'Order Number',     description: 'Associated PO numbers',                                         category: 'Status',    visible: true,                 order: 1  },
  { id: 'date',            name: 'Date',             description: 'Request date',                                                  category: 'Timeline',  visible: true,                 order: 2  },
  { id: 'costCentre',      name: 'Cost Centre',      description: 'Cost centre allocation',                                        category: 'Financial', visible: true,                 order: 3  },
  { id: 'description',     name: 'Description',      description: 'Request description',                                          category: 'Status',    visible: true,  locked: true,  order: 4  },
  { id: 'net',             name: 'Net',              description: 'Net amount excluding VAT',                                      category: 'Financial', visible: false,                order: 5  },
  { id: 'amount',          name: 'Amount',           description: 'Request amount',                                               category: 'Financial', visible: true,  locked: true,  order: 6  },
  { id: 'status',          name: 'Status',           description: 'Approval status',                                              category: 'Status',    visible: true,  locked: true,  order: 7  },
  { id: 'category',        name: 'Category',         description: 'Purchase category',                                            category: 'Status',    visible: false,                order: 8  },
  { id: 'requestedBy',     name: 'Requested By',     description: 'Employee who raised the request',                              category: 'Resource',  visible: true,                 order: 9  },
  { id: 'daysOpen',        name: 'Days Open',        description: 'Age of open requests',                                         category: 'Timeline',  visible: true,                 order: 10 },
  { id: 'currentApprover', name: 'Current Approver', description: 'Who the request is with for approval (in-flight requests only)', category: 'Resource', visible: false,                order: 11 },
  { id: 'lastApprover',    name: 'Last Approver',    description: 'Who last acted on the request',                                category: 'Resource',  visible: false,                order: 12 },
  { id: 'supplier',        name: 'Supplier',         description: 'Supplier name',                                                category: 'Resource',  visible: false,                order: 13 },
  { id: 'currentStage',    name: 'Current Stage',    description: 'Current approval stage',                                      category: 'Status',    visible: false,                order: 14 },
];

// ─── Main component ────────────────────────────────────────────────────────────

export function PurchasesCommitmentsTab({ projectId, projectCode, instanceId, employees = [] }: PurchasesCommitmentsTabProps) {
  const { currentCompany } = useCompany();

  // ── Data state ────────────────────────────────────────────────────────
  const [requests, setRequests] = useState<PurchaseRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFinOpsMode, setIsFinOpsMode] = useState(false);
  const [finOpsNotConfigured, setFinOpsNotConfigured] = useState(false);

  // ── UI state ──────────────────────────────────────────────────────────
  const [selectedRequest, setSelectedRequest] = useState<PurchaseRequest | null>(null);

  // ── Search / filter state ─────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [advancedFilters, setAdvancedFilters] = useState<PRFilters>({});
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  // Derive quick-filter values from the single advancedFilters source of truth
  const quickStatusFilter = useMemo(() => {
    const f = advancedFilters.status;
    return f?.kind === 'multi-select' ? f.selected : [];
  }, [advancedFilters]);

  const quickSupplierFilter = useMemo(() => {
    const f = advancedFilters.supplier;
    return f?.kind === 'multi-select' ? f.selected : [];
  }, [advancedFilters]);

  const setQuickStatusFilter = useCallback((selected: string[]) => {
    setAdvancedFilters((prev) => {
      const next = { ...prev };
      if (selected.length > 0) {
        next.status = { kind: 'multi-select', selected };
      } else {
        delete next.status;
      }
      return next;
    });
  }, []);

  const setQuickSupplierFilter = useCallback((selected: string[]) => {
    setAdvancedFilters((prev) => {
      const next = { ...prev };
      if (selected.length > 0) {
        next.supplier = { kind: 'multi-select', selected };
      } else {
        delete next.supplier;
      }
      return next;
    });
  }, []);

  // ── Table state ───────────────────────────────────────────────────────
  const [columnSorts, setColumnSorts] = useState<ColumnSort[]>([{ columnId: 'date', direction: 'desc' }]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);

  // ── Group By state ────────────────────────────────────────────────────
  const [groupBy, setGroupBy] = useState<PRGroupById | ''>('');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // ── Column preferences ────────────────────────────────────────────────
  const { columns: prColumns, updateColumns } = useColumnPreferences({
    storageKey: 'purchase-requests-columns',
    defaultColumns: DEFAULT_PR_COLUMNS,
  });

  const visibleColumns = useMemo(
    () => prColumns.filter((c) => c.visible).sort((a, b) => a.order - b.order),
    [prColumns]
  );

  // ─── Employee name resolver ──────────────────────────────────────────────────

  const getEmployeeNameByExternalUserId = (externalUserId: string): string => {
    if (!externalUserId) return 'Unknown Employee';
    const employee = employees.find(
      (e) => e.externalUserId?.toLowerCase() === externalUserId.toLowerCase()
    );
    return employee?.name || externalUserId;
  };

  // ─── Fetch data ──────────────────────────────────────────────────────────────

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      setFinOpsNotConfigured(false);

      const dataMode = getDataMode();
      const companyId = currentCompany?.id;
      const financialsInstanceId = currentCompany?.financialsInstanceId;

      const finOpsAvailable = dataMode === 'live'
        ? !!financialsInstanceId
        : isFinOpsIntegrationAvailable(companyId);

      if (dataMode === 'live' && projectCode) {
        if (finOpsAvailable) {
          setIsFinOpsMode(true);
          try {
            const finOpsData = await fetchFinOpsPurchaseRequests(projectCode, projectId, companyId, financialsInstanceId);
            setRequests(finOpsData ?? []);
          } catch {
            console.warn('[PurchasesCommitmentsTab] FinOps API unavailable, showing empty state');
            setRequests([]);
          }
          return;
        } else {
          setFinOpsNotConfigured(true);
          setRequests([]);
          return;
        }
      }

      if (dataMode === 'live') {
        setRequests([]);
        return;
      }

      setIsFinOpsMode(false);
      const data = await fetchPurchaseRequests(projectId);
      setRequests(data);
    } catch (err) {
      setError('Unable to load purchase data. Please try again later.');
      console.error('Error fetching purchase requests:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [projectId, projectCode, currentCompany?.id, currentCompany?.financialsInstanceId]);

  // ─── Derived data ────────────────────────────────────────────────────────────

  const suppliers = useMemo(() => {
    const unique = Array.from(new Set(requests.map((r) => r.supplier).filter(Boolean))).sort();
    return unique.map((s) => {
      const match = requests.find((r) => r.supplier === s);
      const name = match?.supplierName || getEmployeeNameByExternalUserId(s);
      const isDuplicate = name === s;
      return { value: s, label: isDuplicate ? s : `${s} — ${name}`, code: s, name };
    });
  }, [requests, employees]);

  const categories = useMemo(
    () => Array.from(new Set(requests.map((r) => r.category).filter(Boolean))).sort(),
    [requests]
  );

  const statuses = useMemo(
    () => Array.from(new Set(requests.map((r) => r.status).filter(Boolean))).sort(),
    [requests]
  );

  const requestedByOptions = useMemo(() => {
    const seen = new Map<string, string>();
    requests.forEach((r) => {
      if (r.requestedBy && !seen.has(r.requestedBy)) {
        seen.set(r.requestedBy, getEmployeeNameByExternalUserId(r.requestedBy));
      }
    });
    return Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [requests, employees]);

  const costCentreOptions = useMemo(() => {
    const seen = new Map<string, string>();
    requests.forEach((r) => {
      if (r.costCentreCode && !seen.has(r.costCentreCode)) {
        seen.set(r.costCentreCode, r.costCentreName ? `${r.costCentreCode} — ${r.costCentreName}` : r.costCentreCode);
      }
    });
    return Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }, [requests]);

  const currentStageOptions = useMemo(() => {
    const seen = new Set<string>();
    requests.forEach((r) => {
      const stage = getCurrentApprovalStage(r);
      if (stage) seen.add(stage);
    });
    return Array.from(seen).sort();
  }, [requests]);

  const currentApproverOptions = useMemo(() => {
    const seen = new Set<string>();
    requests.forEach((r) => {
      const approver = getCurrentApprover(r);
      if (approver) seen.add(approver);
    });
    return Array.from(seen).sort();
  }, [requests]);

  const lastApproverOptions = useMemo(() => {
    const seen = new Set<string>();
    requests.forEach((r) => {
      const last = getLastApprover(r);
      if (last) seen.add(last);
    });
    return Array.from(seen).sort();
  }, [requests]);

  const summary: PurchaseRequestSummary = useMemo(() => {
    // COMMITTED COST DEFINITION:
    // A purchase is considered "committed" when it has been Posted to the finance system
    // but has NOT yet been fully invoiced/paid (i.e. the commitment has not been relieved).
    //
    // Logic:
    //   - status === 'Posted'           → sent to finance system, PO raised — commitment begins
    //   - isCommitmentRelieved === true → fully invoiced/paid — commitment relieved, becomes actual spend
    //
    // Approved (not yet Posted) is intentionally excluded — the PR is authorised but not yet
    // a formal commitment in the finance system.
    //
    // ⚠️  ENGINEERING NOTE: The `isCommitmentRelieved` flag must be provided by the Financial
    //     Operations API. See ProjectTransChanges_11052026.md UI-PTX-008 for full spec.
    const committed = requests.filter((r) => r.status === 'Posted' && !r.isCommitmentRelieved);
    const pending   = requests.filter((r) => r.status === 'Pending' || r.status === 'Awaiting Approval' || r.status === 'Reopened' || r.status === 'Redirected');
    const rejected  = requests.filter((r) => r.status === 'Rejected' || r.status === 'Cancelled');
    const approved  = requests.filter((r) => r.status === 'Approved' || r.status === 'Posted');
    return {
      totalCommittedCost:    committed.reduce((s, r) => s + r.amount, 0),
      totalApprovedRequests: approved.length,
      totalPendingRequests:  pending.length,
      totalRejectedRequests: rejected.length,
      totalInFlightCost:     pending.reduce((s, r) => s + r.amount, 0),
      totalRejectedCost:     rejected.reduce((s, r) => s + r.amount, 0),
      totalRequestsValue:    requests.reduce((s, r) => s + r.amount, 0),
      averageRequestAmount:  0, // unused
    };
  }, [requests]);

  const activeFilterCount = countActivePRFilters(advancedFilters);

  // ─── Quick filter options ─────────────────────────────────────────────────────

  const STATUS_OPTIONS = useMemo(() => {
    const seen = new Set<string>();
    requests.forEach((r) => seen.add(r.status));
    return Array.from(seen).sort().map((s) => ({ value: s, label: s }));
  }, [requests]);

  const SUPPLIER_OPTIONS = useMemo(() => {
    const seen = new Map<string, string>();
    requests.forEach((r) => {
      if (!seen.has(r.supplier)) {
        seen.set(r.supplier, r.supplierName || getEmployeeNameByExternalUserId(r.supplier));
      }
    });
    return Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [requests, employees]);

  // ─── Filter + sort ───────────────────────────────────────────────────────────

  const filteredAndSorted = useMemo(() => {
    // 1. Text search
    let result = requests.filter((r) => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        r.requestId.toLowerCase().includes(q) ||
        r.description.toLowerCase().includes(q) ||
        r.supplier.toLowerCase().includes(q) ||
        (r.supplierName || getEmployeeNameByExternalUserId(r.supplier)).toLowerCase().includes(q) ||
        r.category.toLowerCase().includes(q) ||
        r.requestedBy.toLowerCase().includes(q) ||
        getEmployeeNameByExternalUserId(r.requestedBy).toLowerCase().includes(q)
      );
    });

    // 2. Advanced filters (includes status + supplier from quick filters)
    result = applyPRFilters(result, advancedFilters, getEmployeeNameByExternalUserId, {
      getCurrentApprover,
      getLastApprover,
      getCurrentStage: getCurrentApprovalStage,
      calculateAge: calculatePRAge,
    });

    // 3. Multi-column sort (quick status/supplier are inside advancedFilters)
    result = [...result].sort((a, b) => {
      for (const sort of columnSorts) {
        let aVal: string | number;
        let bVal: string | number;

        switch (sort.columnId) {
          case 'requestId':    aVal = a.requestId;    bVal = b.requestId;    break;
          case 'description':  aVal = a.description;  bVal = b.description;  break;
          case 'status':       aVal = a.status;       bVal = b.status;       break;
          case 'amount':       aVal = a.amount;       bVal = b.amount;       break;
          case 'supplier':
            aVal = a.supplierName || getEmployeeNameByExternalUserId(a.supplier);
            bVal = b.supplierName || getEmployeeNameByExternalUserId(b.supplier);
            break;
          case 'date':
            aVal = new Date(a.date).getTime();
            bVal = new Date(b.date).getTime();
            break;
          case 'requestedBy':
            aVal = getEmployeeNameByExternalUserId(a.requestedBy);
            bVal = getEmployeeNameByExternalUserId(b.requestedBy);
            break;
          case 'category':     aVal = a.category;     bVal = b.category;     break;
          case 'currentStage':
            aVal = getCurrentApprovalStage(a);
            bVal = getCurrentApprovalStage(b);
            break;
          case 'costCentre':      aVal = a.costCentreCode ?? ''; bVal = b.costCentreCode ?? ''; break;
          case 'currentApprover': aVal = getCurrentApprover(a); bVal = getCurrentApprover(b); break;
          case 'lastApprover':    aVal = getLastApprover(a);    bVal = getLastApprover(b);    break;
          case 'net':             aVal = a.amount;               bVal = b.amount;               break;
          case 'daysOpen':        aVal = calculatePRAge(a) ?? -1; bVal = calculatePRAge(b) ?? -1; break;
          case 'orderNumber':     aVal = a.poNumber?.[0] ?? ''; bVal = b.poNumber?.[0] ?? ''; break;
          default: continue;
        }

        const cmp =
          typeof aVal === 'number' && typeof bVal === 'number'
            ? aVal - bVal
            : String(aVal).localeCompare(String(bVal));

        if (cmp !== 0) return sort.direction === 'asc' ? cmp : -cmp;
      }
      return 0;
    });

    return result;
  }, [requests, searchQuery, advancedFilters, columnSorts, employees]);

  // Reset page on filter/sort change
  useEffect(() => { setCurrentPage(1); }, [searchQuery, advancedFilters, columnSorts, itemsPerPage, groupBy]);

  // ─── Pagination ──────────────────────────────────────────────────────────────

  const pagination: PaginationMetadata = useMemo(
    () => calculatePagination(filteredAndSorted.length, currentPage, itemsPerPage),
    [filteredAndSorted.length, currentPage, itemsPerPage]
  );
  const paginatedRequests = useMemo(
    () => paginateArray(filteredAndSorted, currentPage, itemsPerPage).data,
    [filteredAndSorted, currentPage, itemsPerPage]
  );

  // ─── Grouped data ────────────────────────────────────────────────────────────

  const groupedData = useMemo((): PurchaseRequestGroup[] | null => {
    if (!groupBy) return null;
    return groupPurchaseRequests(
      filteredAndSorted,
      groupBy,
      getEmployeeNameByExternalUserId,
      getCurrentApprovalStage,
    );
  }, [filteredAndSorted, groupBy, employees]);

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ─── Column definitions ──────────────────────────────────────────────────────

  const columnDefs = useMemo((): ColumnDef<PurchaseRequest, unknown>[] => {
    return visibleColumns.map((col): ColumnDef<PurchaseRequest, unknown> => {
      switch (col.id) {
        case 'requestId':
          return {
            id: 'requestId',
            header: col.name,
            accessorFn: (r) => r.requestId,
            cell: ({ row }) => (
              <span
                className="font-medium text-primary hover:underline cursor-pointer"
                onClick={() => setSelectedRequest(row.original)}
              >
                {row.original.requestId}
              </span>
            ),
          };

        case 'orderNumber':
          return {
            id: 'orderNumber',
            header: col.name,
            enableSorting: true,
            accessorFn: (r) => r.poNumber?.[0] ?? '',
            cell: ({ row }) => {
              const po = row.original.poNumber;
              if (!po || po.length === 0) return <span className="text-muted-foreground text-sm">—</span>;
              if (po.length === 1) return <span className="text-sm">{po[0]}</span>;
              return (
                <div className="flex items-center gap-1.5">
                  <span className="text-sm truncate">{po[0]}</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Badge
                        variant="secondary"
                        className="cursor-pointer hover:bg-secondary/80 text-xs px-1.5 py-0 h-5 shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        +{po.length - 1} more
                      </Badge>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-3" align="start">
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground mb-2">Purchase Orders</p>
                        {po.map((p, i) => <div key={i} className="text-sm font-mono">{p}</div>)}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              );
            },
          };

        case 'description':
          return {
            id: 'description',
            header: col.name,
            accessorFn: (r) => r.description,
            cell: ({ row }) => (
              <span className="max-w-[300px] truncate block">{row.original.description}</span>
            ),
          };

        case 'category':
          return {
            id: 'category',
            header: col.name,
            accessorFn: (r) => r.category,
            cell: ({ row }) => <span className="text-sm">{row.original.category}</span>,
          };

        case 'requestedBy':
          return {
            id: 'requestedBy',
            header: col.name,
            accessorFn: (r) => getEmployeeNameByExternalUserId(r.requestedBy),
            cell: ({ row }) => (
              <span className="text-sm">{getEmployeeNameByExternalUserId(row.original.requestedBy)}</span>
            ),
          };

        case 'currentStage':
          return {
            id: 'currentStage',
            header: col.name,
            accessorFn: (r) => getCurrentApprovalStage(r),
            cell: ({ row }) => (
              <span className="text-sm text-muted-foreground">
                {getCurrentApprovalStage(row.original) || '—'}
              </span>
            ),
          };

        case 'costCentre':
          return {
            id: 'costCentre',
            header: col.name,
            accessorFn: (r) => r.costCentreCode ?? '',
            cell: ({ row }) => {
              const r = row.original;
              if (!r.costCentreCode) return <span className="text-muted-foreground">—</span>;
              return (
                <div className="flex flex-col">
                  <span className="font-medium text-sm">{r.costCentreCode}</span>
                  {r.costCentreName && (
                    <span className="text-xs text-muted-foreground">{r.costCentreName}</span>
                  )}
                </div>
              );
            },
          };

        case 'status':
          return {
            id: 'status',
            header: col.name,
            enableSorting: true,
            accessorFn: (r) => r.status,
            cell: ({ row }) => {
              const cfg = getStatusConfig(row.original.status);
              const Icon = cfg.icon;
              return (
                <Badge
                  variant={cfg.variant}
                  accent={cfg.accent}
                  className="gap-1 cursor-pointer hover:opacity-80"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedRequest(row.original);
                  }}
                >
                  <Icon className="h-3 w-3" />
                  {row.original.status}
                </Badge>
              );
            },
          };

        case 'amount':
          return {
            id: 'amount',
            header: col.name,
            accessorFn: (r) => r.amount,
            meta: { align: 'right' },
            cell: ({ row }) => (
              <span className="font-medium">
                {formatCurrency(row.original.amount, row.original.currency)}
              </span>
            ),
          };

        case 'supplier': {
          return {
            id: 'supplier',
            header: col.name,
            accessorFn: (r) => r.supplierName || getEmployeeNameByExternalUserId(r.supplier),
            cell: ({ row }) => {
              const code = row.original.supplier;
              const name = row.original.supplierName || getEmployeeNameByExternalUserId(code);
              const isDuplicate = name === code;
              return (
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{name}</span>
                  {!isDuplicate && <span className="text-xs text-muted-foreground">{code}</span>}
                </div>
              );
            },
          };
        }

        case 'date':
          return {
            id: 'date',
            header: col.name,
            accessorFn: (r) => r.date,
            cell: ({ row }) => (
              <span className="text-sm">{format(new Date(row.original.date), 'dd MMM yyyy')}</span>
            ),
          };

        case 'daysOpen':
          return {
            id: 'daysOpen',
            header: col.name,
            accessorFn: (r) => calculatePRAge(r) ?? -1,
            cell: ({ row }) => {
              const age = calculatePRAge(row.original);
              if (age === null) return <span className="text-muted-foreground text-sm">—</span>;
              const isOld = age > 30;
              return (
                <Badge
                  variant={isOld ? 'destructive' : 'default'}
                  accent={isOld ? 'destructive' : 'info'}
                  className="text-xs"
                >
                  {age} {age === 1 ? 'day' : 'days'}
                </Badge>
              );
            },
          };

        case 'currentApprover':
          return {
            id: 'currentApprover',
            header: col.name,
            accessorFn: (r) => getCurrentApprover(r),
            cell: ({ row }) => {
              const approver = getCurrentApprover(row.original);
              if (!approver) return <span className="text-muted-foreground text-sm">—</span>;
              return <span className="text-sm">{approver}</span>;
            },
          };

        case 'lastApprover':
          return {
            id: 'lastApprover',
            header: col.name,
            accessorFn: (r) => getLastApprover(r),
            cell: ({ row }) => {
              const lastEntry = row.original.approvalHistory
                ? [...row.original.approvalHistory].sort(
                    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
                  )[0]
                : null;
              const name = getLastApprover(row.original);
              if (!name) return <span className="text-muted-foreground text-sm">—</span>;
              return (
                <div className="flex flex-col">
                  <span className="text-sm">{name}</span>
                  {lastEntry && (
                    <span className="text-xs text-muted-foreground">
                      {lastEntry.action} · {format(new Date(lastEntry.date), 'dd MMM yyyy')}
                    </span>
                  )}
                </div>
              );
            },
          };

        case 'net':
          return {
            id: 'net',
            header: col.name,
            accessorFn: (r) => r.amount,
            meta: { align: 'right' },
            cell: ({ row }) => (
              <span className="font-medium">
                {formatCurrency(row.original.amount, row.original.currency)}
              </span>
            ),
          };

        default:
          return {
            id: col.id,
            header: col.name,
            accessorFn: () => '',
            cell: () => null,
          };
      }
    });
  }, [visibleColumns, employees]);

  // ─── Currency formatter ──────────────────────────────────────────────────────

  const formatCurrency = (amount: number, currency = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
    }).format(amount) + ` ${currency}`;
  };

  // ─── Export to Excel ─────────────────────────────────────────────────────────

  const handleExportExcel = useCallback(() => {
    // Column widths keyed by column id
    const colWidths: Record<string, number> = {
      requestId:    14,
      orderNumber:  18,
      description:  36,
      category:     16,
      requestedBy:  22,
      currentStage: 20,
      costCentre:   24,
      status:       14,
      amount:       14,
      supplier:     24,
      date:         14,
      daysOpen:        12,
      currentApprover: 24,
      lastApprover:    24,
      net:             14,
    };

    // Value extractor keyed by column id — mirrors what the table renders
    const getValue = (col: ColumnDefinition, r: PurchaseRequest): string | number => {
      switch (col.id) {
        case 'requestId':    return r.requestId;
        case 'orderNumber':  return r.poNumber?.join(', ') ?? '';
        case 'description':  return r.description;
        case 'category':     return r.category;
        case 'requestedBy':  return getEmployeeNameByExternalUserId(r.requestedBy);
        case 'currentStage': return getCurrentApprovalStage(r) || '';
        case 'costCentre':   return r.costCentreCode
          ? `${r.costCentreCode}${r.costCentreName ? ' — ' + r.costCentreName : ''}`
          : '';
        case 'status':       return r.status;
        case 'amount':       return r.amount;
        case 'supplier': {
          const sName = r.supplierName || getEmployeeNameByExternalUserId(r.supplier);
          return sName === r.supplier ? sName : `${sName} (${r.supplier})`;
        }
        case 'date':         return format(new Date(r.date), 'dd/MM/yyyy');
        case 'daysOpen':        return calculatePRAge(r) ?? '';
        case 'currentApprover': return getCurrentApprover(r);
        case 'lastApprover': {
          const name = getLastApprover(r);
          if (!name) return '';
          const lastEntry = r.approvalHistory
            ? [...r.approvalHistory].sort(
                (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
              )[0]
            : null;
          return lastEntry
            ? `${name} (${lastEntry.action} · ${format(new Date(lastEntry.date), 'dd/MM/yyyy')})`
            : name;
        }
        case 'net':     return r.amount;
        default:                return '';
      }
    };

    const rows = filteredAndSorted.map((r) =>
      Object.fromEntries(visibleColumns.map((col) => [col.name, getValue(col, r)]))
    );

    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet['!cols'] = visibleColumns.map((col) => ({ wch: colWidths[col.id] ?? 16 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Purchase Requests');

    const safeName = (projectCode ?? `project-${projectId}`).replace(/[^a-z0-9]/gi, '-');
    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `Purchase-Requests-${safeName}-${today}.xlsx`);
  }, [filteredAndSorted, visibleColumns, projectCode, projectId, employees]);

  // ─── Loading state ───────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center divide-x">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex-1 px-6 first:pl-4 last:pr-4">
                  <Skeleton className="h-3 w-24 mb-2" />
                  <Skeleton className="h-6 w-32" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><Skeleton className="h-6 w-48" /></CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Error state ─────────────────────────────────────────────────────────────

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-destructive/10 p-3">
                <AlertCircle className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Error Loading Data</h3>
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
              <Button onClick={loadData}>Retry</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── FinOps not configured ────────────────────────────────────────────────────

  if (finOpsNotConfigured) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-muted p-3">
                <Link2Off className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">FinOps Integration Not Configured</h3>
                <p className="text-sm text-muted-foreground">
                  The FinOps integration has not been set up for this company. Please configure the
                  integration in Settings to view purchase requests.
                </p>
              </div>
              <Button variant="outline" onClick={loadData}>Retry</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Empty state ──────────────────────────────────────────────────────────────

  if (requests.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-muted p-3">
                <FileText className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">No Purchase Requests</h3>
                <p className="text-sm text-muted-foreground">
                  There are no purchase requests associated with this project yet.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Main render ──────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Compact Financial Summary Bar */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center divide-x">
            <div className="flex-1 px-6 first:pl-4">
              <KPI
                variant="mini"
                label="Committed Cost"
                value={formatCurrency(summary.totalCommittedCost)}
                description={`${summary.totalApprovedRequests} approved / posted`}
                icon={<TrendingUp className="w-4 h-4" />}
                accent="success"
              />
            </div>
            <div className="flex-1 px-6">
              <KPI
                variant="mini"
                label="Total Logged"
                value={formatCurrency(summary.totalRequestsValue)}
                description={`Across all ${requests.length} requests`}
                icon={<Banknote className="w-4 h-4" />}
                accent="info"
              />
            </div>
            <div className="flex-1 px-6">
              <KPI
                variant="mini"
                label="Awaiting Approval"
                value={formatCurrency(summary.totalInFlightCost)}
                description={`${summary.totalPendingRequests} requests in-flight`}
                icon={<CircleDashed className="w-4 h-4" />}
                accent="warning"
              />
            </div>
            <div className="flex-1 px-6 last:pr-4">
              <KPI
                variant="mini"
                label="Rejected / Cancelled"
                value={formatCurrency(summary.totalRejectedCost)}
                description={`${summary.totalRejectedRequests} requests`}
                icon={<Ban className="w-4 h-4" />}
                accent="destructive"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Table Card */}
      <Card>
        <CardHeader>
          <CardTitle>Purchase Requests</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Toolbar */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {/* Search */}
            <div className="relative w-52 shrink-0">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search requests..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>

            {/* Quick filter — Status */}
            <div className="w-44 shrink-0">
              <MultiSelect
                options={STATUS_OPTIONS}
                placeholder="Status"
                value={quickStatusFilter}
                onValueChange={setQuickStatusFilter}
                maxDisplay={1}
                size="sm"
              />
            </div>

            {/* Quick filter — Supplier */}
            <div className="w-48 shrink-0">
              <MultiSelect
                options={SUPPLIER_OPTIONS}
                placeholder="Supplier"
                value={quickSupplierFilter}
                onValueChange={setQuickSupplierFilter}
                maxDisplay={1}
                size="sm"
              />
            </div>

            {/* Clear quick filters — only visible when active */}
            {(quickStatusFilter.length > 0 || quickSupplierFilter.length > 0) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-xs text-muted-foreground shrink-0"
                onClick={() => { setQuickStatusFilter([]); setQuickSupplierFilter([]); }}
              >
                Clear
              </Button>
            )}

            {/* Spacer */}
            <div className="flex-1" />

            {/* Group By */}
            <div className="w-44 shrink-0">
              <MultiSelect
                options={PR_GROUP_BY_OPTIONS.map((o) => ({ value: o.id, label: o.label }))}
                placeholder="Group by..."
                value={groupBy ? [groupBy] : []}
                onValueChange={(v) => {
                  setGroupBy((v[v.length - 1] ?? '') as PRGroupById | '');
                  setCollapsedGroups(new Set());
                }}
                maxDisplay={1}
                size="sm"
              />
            </div>

            {/* Advanced Filter button */}
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0 relative shrink-0"
              aria-label="Advanced Filters"
              onClick={() => setFilterSheetOpen(true)}
            >
              <Filter className="h-3.5 w-3.5" />
              {activeFilterCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 text-[10px] font-semibold text-primary-foreground min-w-[18px] h-[18px] rounded-full bg-primary inline-flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </Button>

            {/* Column configurator button */}
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0 shrink-0"
              aria-label="Configure columns"
              onClick={() => setColumnConfigOpen(true)}
            >
              <Settings2 className="h-3.5 w-3.5" />
            </Button>

            {/* Export to Excel */}
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0 shrink-0"
              aria-label="Export to Excel"
              onClick={handleExportExcel}
              disabled={filteredAndSorted.length === 0}
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
          </div>

          {/* Results count */}
          <p className="text-sm text-muted-foreground mb-3">
            {groupedData
              ? `${groupedData.length} group${groupedData.length !== 1 ? 's' : ''} · ${filteredAndSorted.length} request${filteredAndSorted.length !== 1 ? 's' : ''}`
              : `Showing ${paginatedRequests.length} of ${filteredAndSorted.length} requests`}
          </p>

          {/* Grouped view */}
          {groupedData ? (
            <div className="space-y-3">
              {groupedData.map((group) => {
                const isCollapsed = collapsedGroups.has(group.key);
                const ChevronIcon = isCollapsed ? ChevronRight : ChevronDown;
                return (
                  <div key={group.key} className="border rounded-lg overflow-hidden">
                    {/* Group header */}
                    <button
                      className="w-full flex items-center gap-3 px-4 py-2.5 bg-muted/40 hover:bg-muted/70 transition-colors text-left"
                      onClick={() => toggleGroup(group.key)}
                    >
                      <ChevronIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm flex-1">{group.label}</span>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground shrink-0">
                        <span>{group.subtotals.count} request{group.subtotals.count !== 1 ? 's' : ''}</span>
                        <span className="font-medium text-foreground">
                          {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(group.subtotals.totalAmount)}
                        </span>
                        {group.subtotals.pendingAmount > 0 && (
                          <span className="text-warning font-medium">
                            {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(group.subtotals.pendingAmount)} pending
                          </span>
                        )}
                        {group.subtotals.approvedAmount > 0 && (
                          <span className="text-success font-medium">
                            {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(group.subtotals.approvedAmount)} approved
                          </span>
                        )}
                      </div>
                    </button>
                    {/* Group rows */}
                    {!isCollapsed && (
                      <DataTableEnhanced<PurchaseRequest, unknown>
                        columns={columnDefs}
                        data={group.requests}
                        columnVisibility={columnDefinitionsToVisibility(visibleColumns)}
                        columnSorts={columnSorts}
                        onColumnSortChange={(s) => { setColumnSorts(s); setCurrentPage(1); }}
                        emptyMessage={
                          <div className="flex flex-col items-center gap-2 py-2">
                            <FileText className="h-8 w-8 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">No requests match your filters</p>
                          </div>
                        }
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            /* Flat table view */
            <DataTableEnhanced<PurchaseRequest, unknown>
              columns={columnDefs}
              data={paginatedRequests}
              columnVisibility={columnDefinitionsToVisibility(visibleColumns)}
              columnSorts={columnSorts}
              onColumnSortChange={(s) => { setColumnSorts(s); setCurrentPage(1); }}
              emptyMessage={
                <div className="flex flex-col items-center gap-2 py-2">
                  <FileText className="h-8 w-8 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">No requests match your filters</p>
                </div>
              }
              paginationContent={
                <PaginationControls
                  pagination={pagination}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={(s) => { setItemsPerPage(s); setCurrentPage(1); }}
                />
              }
            />
          )}
        </CardContent>
      </Card>

      {/* Purchase Request Detail Panel */}
      <PurchaseRequestFullModal
        purchaseRequest={selectedRequest}
        allPurchaseRequests={filteredAndSorted}
        open={!!selectedRequest}
        onOpenChange={(open) => { if (!open) setSelectedRequest(null); }}
        onPurchaseRequestChange={setSelectedRequest}
        projectCode={projectCode}
        instanceId={instanceId}
        onFetchLineDetails={projectCode
          ? (_code, iid) => fetchFinOpsPurchaseRequestDetails(projectCode, iid)
          : undefined}
        resolveEmployeeName={getEmployeeNameByExternalUserId}
      />

      {/* Column Configurator */}
      <ColumnConfiguratorDrawer
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={prColumns}
        onSave={updateColumns}
        defaultColumns={DEFAULT_PR_COLUMNS}
        pageKey="purchase-requests"
      />

      {/* Advanced Filter Sheet */}
      <PurchaseRequestFilterSheet
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
        filters={advancedFilters}
        onApply={(f) => { setAdvancedFilters(f); setCurrentPage(1); }}
        onClear={() => { setAdvancedFilters({}); setCurrentPage(1); }}
        availableStatuses={statuses}
        availableSuppliers={suppliers}
        availableCategories={visibleColumns.some((c) => c.id === 'category') ? categories : []}
        availableRequestedBy={visibleColumns.some((c) => c.id === 'requestedBy') ? requestedByOptions : []}
        availableCostCentres={visibleColumns.some((c) => c.id === 'costCentre') ? costCentreOptions : []}
        availableCurrentStages={visibleColumns.some((c) => c.id === 'currentStage') ? currentStageOptions : []}
        availableCurrentApprovers={visibleColumns.some((c) => c.id === 'currentApprover') ? currentApproverOptions : []}
        availableLastApprovers={visibleColumns.some((c) => c.id === 'lastApprover') ? lastApproverOptions : []}
      />
    </div>
  );
}
